import React from 'react';
import { Box, Typography } from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { formatDate, MOMENT_FORMATS } from '../../../utils/dateFormat';
import { ARIA_CARD_ACCENT } from '../ariaMessageTypes';

const RATING_LABELS = {
  1: 'Not for me',
  2: 'Could be better',
  3: 'Good event',
  4: 'Great event!',
  5: 'An amazing event!',
};

const formatCheckinDateLine = (eventDate) => {
  const raw = String(eventDate ?? '').trim();
  if (!raw) return null;
  return formatDate(raw, MOMENT_FORMATS.DATE_LONG) || raw;
};

/**
 * Post-event check-in body embedded in the message bubble (no nested card).
 * The question line comes from message.text — this only renders event meta + rating.
 */
const AriaPostEventCheckinCard = ({ checkin, isOutbound = false, compact = false }) => {
  if (!checkin?.eventTitle && !checkin?.eventId) return null;

  const maxRating = Number(checkin.maxRating) > 0 ? Number(checkin.maxRating) : 5;
  const hasSubmittedRating = Boolean(checkin.submitted) && checkin.submittedRating != null;
  const ratingValue = hasSubmittedRating
    ? Math.min(maxRating, Math.max(0, Number(checkin.submittedRating)))
    : 0;
  const ratingLabel = hasSubmittedRating
    ? (RATING_LABELS[ratingValue] ?? 'Thanks for sharing!')
    : null;
  const dateLine = formatCheckinDateLine(checkin.eventDate);
  const primaryColor = isOutbound ? '#ffffff' : 'text.primary';
  const secondaryColor = isOutbound ? 'rgba(255,255,255,0.78)' : 'text.secondary';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1, width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
        {checkin.coverImageUrl ? (
          <Box
            component="img"
            src={checkin.coverImageUrl}
            alt=""
            sx={{
              width: compact ? 44 : 48,
              height: compact ? 44 : 48,
              borderRadius: '10px',
              objectFit: 'cover',
              flexShrink: 0,
              bgcolor: isOutbound ? 'rgba(255,255,255,0.12)' : 'action.hover',
            }}
          />
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: compact ? '0.75rem' : '0.8125rem',
              fontWeight: 700,
              color: primaryColor,
              lineHeight: 1.35,
            }}
          >
            {checkin.eventTitle}
          </Typography>
          {dateLine ? (
            <Typography
              noWrap
              sx={{
                mt: 0.125,
                fontSize: compact ? '0.625rem' : '0.6875rem',
                color: secondaryColor,
                fontWeight: 500,
                lineHeight: 1.35,
              }}
            >
              {dateLine}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 0.25,
            mb: hasSubmittedRating ? 0.375 : 0,
          }}
        >
          {Array.from({ length: maxRating }, (_, index) => {
            const filled = index < ratingValue;
            return (
              <StarRoundedIcon
                key={index}
                sx={{
                  fontSize: compact ? 20 : 22,
                  color: filled
                    ? ARIA_CARD_ACCENT
                    : isOutbound
                      ? 'rgba(255,255,255,0.28)'
                      : 'divider',
                }}
              />
            );
          })}
        </Box>
        {hasSubmittedRating ? (
          <Typography
            sx={{
              fontSize: compact ? '0.6875rem' : '0.75rem',
              fontWeight: 600,
              color: isOutbound ? '#ffffff' : ARIA_CARD_ACCENT,
              lineHeight: 1.35,
            }}
          >
            {ratingValue} — {ratingLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default AriaPostEventCheckinCard;
